import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import type { FormValues } from './formfields/formValues';
import type { SavedFile } from './FormFileUploadField';

/** Where to resume a form: server draft, else the last submitted answers. */
interface ResumeState {
  source: 'draft' | 'submission' | 'none';
  values?: FormValues;
  consent?: boolean;
  step?: number;
  /** Field key -> filenames already on file (names only; from last submission). */
  files?: Record<string, string[]>;
  /** Field key -> files persisted into the live draft (name + key, deletable). */
  draft_files?: Record<string, SavedFile[]>;
}

/**
 * Owns a form's resumable working state: seeds from the server draft (or the
 * last submission), debounce-autosaves every change, retries a save the
 * network dropped, and flushes a pending save on unmount or pagehide so the
 * last keystrokes before the page goes away aren't lost.
 */
export function useFormDraft(taskId: string) {
  const [values, setValues] = useState<FormValues>({});
  const [savedFiles, setSavedFiles] = useState<Record<string, SavedFile[]>>({});
  const [consent, setConsent] = useState(false);
  const [step, setStep] = useState(0);
  // Seeded from the server (draft, else last submission) so the form resumes
  // wherever it was left - on any device, after a crash, or after HR reopens.
  const [seeded, setSeeded] = useState(false);

  const resume = useQuery({
    queryKey: ['onboarding-form-resume', taskId],
    queryFn: () => api<ResumeState>(`/api/me/onboarding/forms/${taskId}/draft`),
    // Never serve this from a stale cache: autosave writes to the server but
    // not to this query, so a cached (pre-edit) draft would seed the form and
    // the `seeded` guard would lock it before the refetch landed - the form
    // would come back empty even though the server had the answers. Refetch
    // fresh on every open so we always resume from the authoritative draft.
    gcTime: 0,
    staleTime: 0,
    refetchOnMount: 'always',
  });
  useEffect(() => {
    if (seeded || !resume.data) return;
    setValues(resume.data.values ?? {});
    setConsent(resume.data.consent ?? false);
    setStep(resume.data.step ?? 0);
    setSavedFiles(resume.data.draft_files ?? {});
    setSeeded(true);
  }, [seeded, resume.data]);
  const priorFiles = resume.data?.files ?? {};

  // Latest state mirrored into refs, declared ahead of the autosave because
  // the save reads its payload from here rather than from its arguments: a
  // retry then sends what the form says now, never the older values captured
  // when the attempt that failed was queued.
  const latestRef = useRef({ values, consent, step });
  latestRef.current = { values, consent, step };
  const seededRef = useRef(seeded);
  seededRef.current = seeded;
  const submittedRef = useRef(false);
  // Payload last known to have reached the server, so a flush can skip a
  // redundant PUT when nothing has changed since.
  const savedRef = useRef<string | null>(null);

  // Autosave answers to the server as they change (debounced). Guarded on
  // `seeded` so the empty pre-seed state never clobbers a saved draft.
  const saveDraft = useMutation({
    mutationFn: async ({ flush = false }: { flush?: boolean }) => {
      const body = JSON.stringify(latestRef.current);
      const res = await api<{ ok: true }>(`/api/me/onboarding/forms/${taskId}/draft`, {
        method: 'PUT',
        body,
        // A flush races page teardown, and browsers cancel ordinary in-flight
        // fetches on navigate, close or freeze. keepalive lets that request
        // outlive the page. Routine saves stay off it: keepalive bodies share
        // a 64KB per-page budget that a long form could exhaust.
        keepalive: flush,
      });
      savedRef.current = body;
      return res;
    },
    // Phones drop requests mid-form (locking an iPhone freezes the page and
    // kills whatever fetch was in flight). Without a retry one blip discards
    // that save, and the "it will retry as you type" line the form shows only
    // holds while the person is still typing.
    retry: 2,
    retryDelay: (attempt) => 500 * 2 ** attempt,
  });
  const saveRef = useRef(saveDraft);
  saveRef.current = saveDraft;
  useEffect(() => {
    if (!seeded) return;
    const h = setTimeout(() => saveRef.current.mutate({}), 700);
    return () => clearTimeout(h);
  }, [seeded, values, consent, step]);

  // Flush a pending (debounced) autosave when the form goes away, so the last
  // keystrokes aren't lost in the 700ms window. Reads refs only, so the copy
  // captured by the mount-only effect below stays current.
  const flush = useCallback(() => {
    if (!seededRef.current || submittedRef.current) return;
    if (JSON.stringify(latestRef.current) === savedRef.current) return;
    saveRef.current.mutate({ flush: true });
  }, []);

  useEffect(() => {
    // pagehide as well as unmount: on iOS, locking the phone or switching apps
    // freezes the page mid-debounce without unmounting anything, and Safari can
    // then discard it outright. beforeunload/unload do not fire reliably there.
    window.addEventListener('pagehide', flush);
    return () => {
      window.removeEventListener('pagehide', flush);
      flush();
    };
  }, [flush]);

  // Submitted: the unmount that follows must not re-save a draft over the
  // now-complete task.
  function markSubmitted() {
    submittedRef.current = true;
  }

  return {
    isLoading: resume.isLoading,
    seeded,
    values,
    setValues,
    consent,
    setConsent,
    step,
    setStep,
    savedFiles,
    setSavedFiles,
    priorFiles,
    saveDraft,
    markSubmitted,
  };
}
